import { Button, Flex, Text, theme } from '../../2022-component-library';
import { useState, useEffect } from 'react';
import { useTestContext } from '../../context/testContext';
import { useAppointment } from '../../context/appointmentContext';
import { logmarToSnellen } from '../../helpers/data';
import { client } from '../../helpers/api-client';
import { usePostMessage } from '../../context/messageContext';
import { confirmNavigation } from '../../helpers/functions';
import { useResultData } from '../../helpers/useResultData';

const ResponsiveAcuityResults = ({ optimaTest, setActiveTab, goBackToCompletedTests }) => {
  const { testData } = useTestContext();
  const viewingLocalTestResults = testData.leftEye || testData.rightEye || testData.bothEyes;
  const [testResults, setTestResults] = useState({});
  const { appointment } = useAppointment();
  const [viewingOptimaResults, setViewingOptimaResults] = useState(optimaTest ? true : false);
  const postMessage = usePostMessage();
  const { clearAllResults } = useResultData();

  const sendTestResults = () => {
    const { lettersToUse, corrected } = testData;
    let testResultData = { leftEye: 'Eye not tested', rightEye: 'Eye not tested', lettersToUse, corrected };
    if (testData.testRightEye && testData.rightEye) {
      testResultData['rightEye'] = viewingOptimaResults ? testData.rightEye : testData.rightEye.score.toFixed(2);
      if (!viewingOptimaResults)
        testResultData['rightEyeSnellen'] =
          typeof testData.rightEye.score === 'number' ? logmarToSnellen[testData.rightEye.score.toFixed(1)] : testData.rightEye.score;
    }

    if (testData.testLeftEye && testData.leftEye) {
      testResultData['leftEye'] = viewingOptimaResults ? testData.leftEye : testData.leftEye.score.toFixed(2);
      if (!viewingOptimaResults)
        testResultData['leftEyeSnellen'] =
          typeof testData.leftEye.score === 'number' ? logmarToSnellen[testData.leftEye.score.toFixed(1)] : testData.leftEye.score;
    }

    if (testData.testBothEyes && testData.bothEyes) {
      testResultData['bothEyes'] = viewingOptimaResults ? testData.bothEyes : testData.bothEyes.score.toFixed(2);
      if (!viewingOptimaResults)
        testResultData['bothEyesSnellen'] =
          typeof testData.bothEyes.score === 'number' ? logmarToSnellen[testData.bothEyes.score.toFixed(1)] : testData.bothEyes.score;
    }

    const sendTestResultData = {
      appointment_eye_test: testData.eyeTestId,
      appointment: appointment.currentAppointment.id,
      result_data: JSON.stringify(testResultData),
    };

    if (window.confirm('Are you sure you want to save the test results?') === true) {
      client(`api/appointment-test-result/`, { body: sendTestResultData }).then(
        () => {
          setActiveTab('results');
        },
        (error) => {
          console.error('There was an error saving the results:', error);
        },
      );
    }
  };

  const restartTest = () => {
    confirmNavigation('restart').then(() => {
      clearAllResults();
      return postMessage({ type: 'beginTestAgain' });
    });
  };

  const ResultsTable = ({ eye }) => {
    const result = viewingOptimaResults ? testResults[eye] : testResults[eye]?.score;
    const title = (eye.charAt(0).toUpperCase() + eye.slice(1)).match(/[A-Z][a-z]+/g).join(' ');
    const bgColor = eye === 'rightEye' ? theme.colors.primary : '#d1d1d1';
    const contrastingTextColor = eye === 'rightEye' ? 'white' : 'black;';

    return viewingOptimaResults ? (
      <Flex column width='40vw' maxWidth='350px' centered borderRight={eye === 'rightEye' ? '1px solid #000' : null}>
        <Flex width='100%' height='5vh' minHeight='60px' centered padding='1rem' backgroundColor={bgColor}>
          <Text size='h2' color={contrastingTextColor}>
            {title}
          </Text>
        </Flex>
        <Flex width='100%' height='5vh' minHeight='60px' centered padding='1rem'>
          <Text size='subHeading'>{result?.textSizeRead ? `Text size read: ${result.textSizeRead}` : 'N/A'}</Text>
        </Flex>
        <Flex column width='100%' height='5vh' minHeight='60px' centered padding='1rem' backgroundColor={bgColor}>
          {result?.magnification ? (
            <>
              <Text size='subHeading' color={contrastingTextColor} textAlign='center'>
                {`Suggested magnification:`}
              </Text>
              <Text size='subHeading' color={contrastingTextColor} textAlign='center'>
                {result.magnification}
              </Text>
            </>
          ) : (
            <Text size='subHeading' color={contrastingTextColor} textAlign='center'>
              {'N/A'}
            </Text>
          )}
        </Flex>
      </Flex>
    ) : (
      <Flex column width='35vw' maxWidth='350px' centered borderRight={eye === 'rightEye' ? '1px solid #000' : null}>
        <Flex width='100%' centered padding='1rem' backgroundColor={bgColor}>
          <Text size='h2' color={contrastingTextColor}>
            {title}
          </Text>
        </Flex>
        <Flex width='100%' centered padding='1rem'>
          <Text size='subHeading'>{result > 1 ? '>1.00' : result?.toFixed(2) || 'N/A'}</Text>
        </Flex>
        <Flex width='100%' centered padding='1rem' backgroundColor={bgColor}>
          <Text size='subHeading' color={contrastingTextColor}>
            {logmarToSnellen[result?.toFixed(1)] || 'N/A'}
          </Text>
        </Flex>
        <Flex width='100%' centered padding='1rem'>
          <Text size='h2'>Snellen</Text>
        </Flex>
      </Flex>
    );
  };

  useEffect(() => {
    const getTestResultsFromServer = async () => {
      const testId = testData.testResultsId;
      client(`api/appointment-test-result/${testId}`).then(
        (data) => {
          if (data.test_key === 'optimaChart') {
            setViewingOptimaResults(true);
            const parsedResults = data.result_data ? JSON.parse(data.result_data) : [];
            setTestResults(parsedResults);
          } else {
            const parsedResults = data.result_data ? JSON.parse(data.result_data) : [];
            for (let eye in parsedResults) {
              if (+parsedResults[eye])
                setTestResults((existingResults) => {
                  return { ...existingResults, [eye]: { score: +parsedResults[eye] } };
                });
            }
          }
        },
        (error) => {
          console.error(error);
        },
      );
    };

    if (viewingLocalTestResults) {
      setTestResults(testData);
    } else {
      getTestResultsFromServer();
    }
  }, [viewingLocalTestResults, testData]);

  return (
    <Flex column centered>
      <Flex centered>
        {testResults.bothEyes ? (
          <ResultsTable eye='bothEyes' />
        ) : (
          <>
            <ResultsTable eye='rightEye' />
            <ResultsTable eye='leftEye' />
          </>
        )}
      </Flex>
      <Flex marginTop='2rem' width='50vw' justifyContent={goBackToCompletedTests ? 'center' : 'space-between'}>
        <Button variant='outlined' width='calc(25vw - 2rem)' onClick={goBackToCompletedTests ? goBackToCompletedTests : () => restartTest()}>
          {goBackToCompletedTests ? 'Back' : 'Restart Test'}
        </Button>
        {!goBackToCompletedTests && (
          <Button width='calc(25vw - 2rem)' onClick={() => sendTestResults()}>
            Save Test Results
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default ResponsiveAcuityResults;
